import { cn } from '@/lib/cn';

/**
 * Espaço reservado com a forma do conteúdo que está chegando.
 *
 * Preferido ao spinner solto: o esqueleto já ocupa o lugar final, então a
 * página não pula quando o dado chega — e comunica *o que* está carregando,
 * não apenas que algo está.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      aria-hidden
      className={cn('relative overflow-hidden rounded-md bg-muted', className)}
    >
      <div
        className={cn(
          'absolute inset-0 -translate-x-full animate-shimmer',
          'bg-gradient-to-r from-transparent via-foreground/10 to-transparent',
          // O reset global reduz a duração da animação, mas esta é infinita:
          // 0,01 ms em laço vira cintilação. Some de vez em vez de piscar.
          'motion-reduce:hidden',
        )}
      />
    </div>
  );
}

/**
 * Região que está carregando.
 *
 * `aria-busy` + `role="status"` para o leitor de tela anunciar a espera — os
 * retângulos são `aria-hidden` e sozinhos não diriam nada.
 */
export function SkeletonRegion({
  label = 'Carregando',
  className,
  children,
}: {
  label?: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div role="status" aria-busy aria-live="polite" className={className}>
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** Linhas de texto de larguras diferentes — parágrafo não é um bloco sólido. */
export function SkeletonText({ lines = 3, className }: { lines?: number; className?: string }) {
  const larguras = ['w-full', 'w-11/12', 'w-9/12', 'w-10/12', 'w-8/12'];
  return (
    <div className={cn('space-y-2', className)}>
      {Array.from({ length: lines }, (_, indice) => (
        <Skeleton key={indice} className={cn('h-3.5', larguras[indice % larguras.length])} />
      ))}
    </div>
  );
}
